import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePageTitle } from '@/hooks/use-page-title';
import { useApplications } from '@/hooks/use-entity-list';
import { useListFilters } from '@/hooks/use-list-filters';
import { useHeaderAction } from '@/lib/header-action-context';
import { Link } from 'react-router';
import { capabilityColors } from '@/lib/capability-colors';
import { CapabilityListPanel } from '@/components/capability-list/CapabilityListPanel';
import { ListPaginationFooter } from '@/components/capability-list/ListPaginationFooter';
import { listControlsGlassTrigger } from '@/components/capability-list/list-controls-classes';
import { cn } from '@/lib/utils';
import AppFormWizard from '@/components/apps/AppFormWizard';
import AppImportDrawer from '@/components/apps/AppImportDrawer';
import {
    Box,
    Clock,
    Database,
    FolderInput,
    Plus,
} from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import type { Application } from '@/types';

const appStyle = {
    accent: capabilityColors.app.border,
    iconBg: capabilityColors.app.bgLight,
    iconColor: capabilityColors.app.text,
    hex: capabilityColors.app.hex,
};

const statusBadgeStyles: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-700',
    suspended: 'bg-amber-100 text-amber-700',
    deleted: 'bg-red-100 text-red-700',
};

const SORT_OPTIONS = [
    { key: 'name', label: 'Name' },
    { key: 'status', label: 'Status' },
    { key: 'created', label: 'Created' },
] as const;

function ApplicationCardInner({
    app,
    showDetail,
    largeText,
}: {
    app: Application;
    showDetail: boolean;
    largeText?: boolean;
}) {
    const badgeStyle = statusBadgeStyles[app.status] || 'bg-slate-100 text-slate-700';
    const badgeText = largeText ? 'text-[9.9px]' : 'text-[9px]';
    const bodyText = largeText ? 'text-[11px]' : 'text-[10px]';
    const titleText = largeText ? 'text-[15.4px]' : 'text-sm';
    const iconSize = largeText ? 20 : 18;
    const inlineIconSize = largeText ? 11 : 10;

    return (
        <>
            {showDetail && (
                <div className="absolute right-2 top-2 z-10 flex items-center gap-1.5">
                    <span
                        className={`rounded-full px-1.5 py-0.5 font-medium uppercase ${badgeText} ${badgeStyle}`}
                    >
                        {app.status}
                    </span>
                </div>
            )}

            <div
                className={
                    showDetail
                        ? 'flex flex-row items-start gap-2'
                        : 'flex h-full min-h-0 w-full flex-row items-start gap-2 overflow-hidden'
                }
            >
                <div
                    className={`flex shrink-0 items-center justify-center rounded-md p-1.5 ${appStyle.iconBg} ${appStyle.iconColor}`}
                >
                    <Box size={iconSize} />
                </div>
                <div
                    className={
                        showDetail
                            ? 'flex min-w-0 flex-1 flex-col'
                            : 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'
                    }
                >
                    <div
                        className={`min-w-0 text-left font-bold leading-tight text-slate-800 ${titleText} ${
                            !showDetail ? 'line-clamp-2' : ''
                        }`}
                    >
                        {app.name || app.app_key}
                    </div>
                    <div
                        className={`text-left font-mono text-slate-500 ${bodyText} ${
                            !showDetail ? 'line-clamp-1' : ''
                        }`}
                    >
                        {app.app_key}
                    </div>
                </div>
            </div>

            {showDetail && (
                <div
                    className={`mt-auto flex items-center gap-1.5 border-t border-slate-100 pt-1.5 text-slate-500 ${bodyText}`}
                >
                    {typeof app.capability_count === 'number' && (
                        <span className="flex items-center gap-1">
                            <Database size={inlineIconSize} />
                            {app.capability_count} capabilities
                        </span>
                    )}
                    <span className="ml-auto flex items-center gap-1 text-slate-400">
                        <Clock size={inlineIconSize} />
                        {new Date(app.created).toLocaleDateString()}
                    </span>
                </div>
            )}
        </>
    );
}

export default function ApplicationsListPage() {
    const queryClient = useQueryClient();
    const { setHeaderAction, setHeaderTitle } = useHeaderAction();
    const { activeFilters, setFilter, filters } = useListFilters();
    const { data, isLoading } = useApplications(activeFilters);
    const [addAppDrawerOpen, setAddAppDrawerOpen] = useState(false);
    const [importDrawerOpen, setImportDrawerOpen] = useState(false);
    usePageTitle('Applications');

    const buttonClass =
        'inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2';

    useEffect(() => {
        setHeaderTitle({
            icon: (
                <div
                    className={`flex h-7 w-7 items-center justify-center rounded-md ${appStyle.iconBg} ${appStyle.iconColor}`}
                >
                    <Box size={16} />
                </div>
            ),
            title: 'Applications',
            subtitle: 'Browse and manage the application capability list.',
        });

        setHeaderAction(
            <div className="ml-auto flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => setImportDrawerOpen(true)}
                    className={buttonClass}
                >
                    <FolderInput size={14} />
                    Import Application
                </button>
                <button
                    type="button"
                    onClick={() => setAddAppDrawerOpen(true)}
                    className={buttonClass}
                >
                    <Plus size={14} />
                    Add Application
                </button>
            </div>,
        );
        return () => {
            setHeaderAction(null);
            setHeaderTitle(null);
        };
    }, [setHeaderAction, setHeaderTitle]);

    const handleSort = (field: string) => {
        if (filters.sort === field) {
            setFilter('direction', filters.direction === 'asc' ? 'desc' : 'asc');
        } else {
            setFilter('sort', field);
            setFilter('direction', 'asc');
        }
    };

    const items: Application[] = data?.data ?? [];
    const meta = data?.meta;
    const allStatuses = useMemo(
        () => Array.from(new Set(items.map((app) => app.status))).sort(),
        [items],
    );

    const statusFilter = (
        <Select
            value={filters.status || 'all'}
            onValueChange={(v) => setFilter('status', v === 'all' ? '' : v)}
        >
            <SelectTrigger className={cn('w-44', listControlsGlassTrigger)}>
                <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {allStatuses.map((status) => (
                    <SelectItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );

    const paginationFooter =
        meta && meta.total > 0 ? (
            <ListPaginationFooter
                meta={meta}
                onPageChange={(page) => setFilter('page', String(page))}
            />
        ) : undefined;

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden" style={{ height: '100%' }}>
            <CapabilityListPanel<Application>
                items={items}
                sortOptions={SORT_OPTIONS.map((option) => ({
                    key: option.key,
                    label: option.label,
                }))}
                sortField={filters.sort}
                sortDirection={filters.direction}
                onSort={handleSort}
                searchQuery={filters.search}
                onSearchChange={(v) => setFilter('search', v)}
                searchPlaceholder="Search applications by name or key..."
                allTags={[]}
                selectedTags={new Set<string>()}
                onToggleTag={() => {}}
                onClearAllTags={() => {}}
                onSelectAllTags={() => {}}
                showTagsFilter={false}
                isLoading={isLoading}
                emptyMessage="No applications found."
                extraToolbar={statusFilter}
                pagination={paginationFooter}
                gridStyle={{
                    gridTemplateColumns: 'repeat(auto-fill, minmax(212px, 258px))',
                    gap: 'clamp(10px, 2vw, 20px)',
                }}
                renderCard={(app) => (
                    <Link
                        key={app.id}
                        to={`/applications/${app.app_key}`}
                        className="group/card relative z-0 block hover:z-40 focus-within:z-40"
                    >
                        <div
                            className={`relative z-0 flex h-[84px] min-h-[84px] w-full cursor-pointer flex-col overflow-hidden rounded-lg border border-slate-200 border-l-4 ${appStyle.accent} bg-white p-3 shadow-sm transition-shadow duration-150 group-hover/card:shadow-none group-focus-within/card:shadow-none hover:border-slate-300 hover:bg-slate-50 focus-within:border-slate-300 focus-within:bg-slate-50`}
                        >
                            <ApplicationCardInner app={app} showDetail={false} />
                        </div>

                        <div className="pointer-events-none absolute left-0 top-0 z-10 min-h-[120%] w-[120%] min-w-0 opacity-0 transition-opacity duration-150 group-hover/card:pointer-events-auto group-hover/card:opacity-100 group-focus-within/card:pointer-events-auto group-focus-within/card:opacity-100">
                            <div
                                className="flex min-h-full w-full cursor-pointer flex-col rounded-xl border-2 border-solid bg-white p-4 hover:bg-slate-50"
                                style={{
                                    borderColor: appStyle.hex,
                                    boxShadow: `0 25px 50px -12px rgb(15 23 42 / 0.2), 0 12px 36px -12px color-mix(in srgb, ${appStyle.hex} 38%, transparent)`,
                                }}
                            >
                                <ApplicationCardInner
                                    app={app}
                                    showDetail
                                    largeText
                                />
                            </div>
                        </div>
                    </Link>
                )}
            />

            <AppFormWizard
                isOpen={addAppDrawerOpen}
                onClose={() => setAddAppDrawerOpen(false)}
                onAppCreated={() => {
                    queryClient.invalidateQueries({ queryKey: ['applications'] });
                }}
            />
            <AppImportDrawer
                isOpen={importDrawerOpen}
                onClose={() => setImportDrawerOpen(false)}
                onImportSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: ['applications'] });
                }}
            />
        </div>
    );
}
